// src/pages/happinessreport/index.tsx
/**
 * Página principal do Relatório de Acompanhamento.
 * - Usa o hook useHappinessReportData para obter dados e handlers.
 * - Compoe a UI com componentes de apresentação.
 *
 * Substitua o seu index.tsx atual por este (mantive as dependências de estilo e componentes).
 */

import React from 'react';
import SidebarResponsiveComp from '../../components/sideBar/siderbar-responsive';
import HeaderManager from '../../components/headerManager';
import { Container, Spinner } from 'react-bootstrap';
import { CustomNav } from '../settings/styles';
import { ContainerDashboard } from './styles';
import { useHappinessReportData } from './hooks/useHappinessReportData';
import FiltersBar from './components/FiltersBar';
import IndicatorsRow from './components/IndicatorsRow';
import ChartsAndDimensions from './components/ChartsAndDimensions';
import FeedbacksAccordion from './components/FeedbacksAccordion';
import ComparativeCharts from './components/ComparativeCharts';
import { representationOptionsValues } from '../../services/utils/representationOptions';
import { useTranslation } from 'react-i18next';
import { getQuestionnaireVersion } from '../../i18n/questionnaire';


const HappinessReportPage: React.FC = () => {
    const { t } = useTranslation('reports');
    const controller = useHappinessReportData();

    const {
        loading,
        dateValue,
        generalData,
        filteredData,
        companyCreatedAt,
        representationSelectedOptions,
        selectedGroupOptions,
        appliedRepresentationSelectedOptions,
        appliedGroupOptions,
        loadingWorkHappinessFeedbacksData,
        loadingFurtherFeelingsFeedbacksData,
        workHappinessFeedbacksPageResults,
        furtherFeelingsFeedbacksPageResults,
        loadWorkHappinessFeedbacks,
        loadFurtherFeelingsFeedbacks
    } = controller;

    const hasFilter = (appliedRepresentationSelectedOptions?.length > 0) || (appliedGroupOptions?.length > 0);

    return (
        <>
            <SidebarResponsiveComp title={t('title')} />
            <HeaderManager title={t('title')} />
            <CustomNav className="mt-1" style={{ margin: '0', border: 'none' }} />

            <Container fluid className='d-flex flex-column'>
                <ContainerDashboard>
                    <FiltersBar controller={controller} />

                    {loading ? (
                        <div className="d-flex align-items-center justify-content-center"style={{ minHeight: '300px' }}>
                            <Spinner style={{ color: '#c9c9c9' }} />
                        </div>
                    ) : (
                        <>
                            <IndicatorsRow
                                loading={loading}
                                dateValue={dateValue}
                                companyCreatedAt={companyCreatedAt}
                                generalData={generalData}
                                filteredData={filteredData}
                                hasFilter={hasFilter}
                            />
                            <ChartsAndDimensions
                                loading={loading}
                                generalData={generalData}
                                filteredData={filteredData}
                                questionnaireVersion={getQuestionnaireVersion(dateValue)}
                            />

                            <div style={{ marginTop: '2em' }}>
                                <h4 style={{ color: '#3C4043', padding: '3em 0 1.2em 0' }}>{t('feedbacks')}</h4>
                                <FeedbacksAccordion
                                    loadingWork={controller.loadingWorkHappinessFeedbacksData}
                                    loadingFurther={controller.loadingFurtherFeelingsFeedbacksData}
                                    workHappinessFeedbacksPageResults={controller.workHappinessFeedbacksPageResults}
                                    furtherFeelingsFeedbacksPageResults={controller.furtherFeelingsFeedbacksPageResults}
                                    loadWorkHappinessFeedbacks={controller.loadWorkHappinessFeedbacks}
                                    loadFurtherFeelingsFeedbacks={controller.loadFurtherFeelingsFeedbacks}
                                    dateValue={controller.dateValue}
                                    representationSelectedOptions={controller.representationSelectedOptions}
                                    selectedGroupOptions={controller.selectedGroupOptions}
                                    representationOptionsValues={representationOptionsValues}
                                    renderDate={controller.renderDate}
                                    isGroupResponsible={controller.isGroupResponsible}
                                />
                            </div>

                            {/* Comparative charts */}
                            <ComparativeCharts
                                loadingSexesData={controller.loadingSexesData}
                                loadingAgeRangesData={controller.loadingAgeRangesData}
                                loadingGroupsData={controller.loadingGroupsData}
                                genderSeries={controller.genderSeries}
                                ageRangesSeries={controller.ageRangesSeries}
                                groupsSeries={controller.groupsSeries}
                                generalData={controller.generalData}
                                filteredData={controller.filteredData}
                                hasFilter={hasFilter}
                            />
                        </>
                    )}
                </ContainerDashboard>
            </Container>
        </>
    );
};

export default HappinessReportPage;
